import { useEffect, useRef, useState } from "react";
import {
  fetchEventSource as defaultPostEventSource,
  EventSourceMessage
} from "@microsoft/fetch-event-source";

interface UseEventSourceResult<T = string> {
  data: T | null;
  error: Event | Error | null;
  isLoading: boolean;
  close: () => void;
}

type AnyRecord = Record<string, unknown>;
type EventControls = { close: () => void };

type EventHandlers<M extends AnyRecord> = {
  [K in keyof M]: (
    data: M[K],
    event: EventSourceMessage,
    controls?: EventControls
  ) => void | Promise<void>;
};

type Fetcher = typeof defaultPostEventSource;

type EventSourceOptions<M extends AnyRecord> = {
  url: string;
  eventKey?: string | Array<unknown>;
  enabled?: boolean;
  eventHandlers: EventHandlers<M>;
  closeOn?: Array<keyof M & string>;
  parse?: (data: string) => M[keyof M];
  body?: unknown;
  fetcher?: Fetcher;
  onOpen?: () => void;
  onClose?: () => void;
  onError?: (error: Event | Error) => void;
};

function usePostEventSource<M extends AnyRecord = Record<string, string>>({
  url,
  eventKey,
  enabled = true,
  eventHandlers,
  closeOn,
  parse,
  body,
  fetcher = defaultPostEventSource,
  onOpen,
  onClose,
  onError
}: EventSourceOptions<M>): UseEventSourceResult<M[keyof M]> {
  const [data, setData] = useState<M[keyof M] | null>(null);
  const [error, setError] = useState<Event | Error | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const controllerRef = useRef<AbortController | null>(null);

  const handlersRef = useRef(eventHandlers);
  const parseRef = useRef(parse);
  const closeOnRef = useRef(closeOn);
  const onOpenRef = useRef(onOpen);
  const onErrorRef = useRef(onError);
  const onCloseRef = useRef(onClose);

  handlersRef.current = eventHandlers;
  parseRef.current = parse;
  closeOnRef.current = closeOn;
  onOpenRef.current = onOpen;
  onErrorRef.current = onError;
  onCloseRef.current = onClose;

  const cacheKey =
    eventKey && Array.isArray(eventKey)
      ? JSON.stringify(eventKey)
      : (eventKey ?? url);

  const close = () => {
    controllerRef.current?.abort();
    controllerRef.current = null;
    onCloseRef.current?.();
  };

  const connect = () => {
    if (!url) return;

    setIsLoading(true);
    setError(null);

    const controller = new AbortController();
    controllerRef.current = controller;

    fetcher(url, {
      method: "POST",
      signal: controller.signal,
      credentials: "include",
      headers: {
        "Content-Type": "application/json"
      },
      body: body ? JSON.stringify(body) : undefined,

      async onopen() {
        setIsLoading(false);
        setError(null);
        onOpenRef.current?.();
      },

      onmessage(msg: EventSourceMessage) {
        const { event, data: raw } = msg;
        if (!event) return;

        const handler = handlersRef.current[event as keyof M];
        if (!handler) return;

        let parsed: M[keyof M];

        try {
          parsed =
            parseRef.current && typeof raw === "string"
              ? parseRef.current(raw)
              : (raw as M[keyof M]);
        } catch (err) {
          setError(err as Error);
          onErrorRef.current?.(err as Error);
          close();
          return;
        }

        setData(parsed);
        handler(parsed, msg, { close });

        if (closeOnRef.current?.includes(event as keyof M & string)) {
          close();
        }
      },

      onerror(err) {
        setError(err);
        onErrorRef.current?.(err);
        close();
      }
    });
  };

  useEffect(() => {
    if (!enabled) return;

    if (!url) {
      const err = new Error("EventSource URL is not provided.");
      setError(err);
      onErrorRef.current?.(err);
      setIsLoading(false);
      return;
    }

    connect();

    return () => {
      close();
    };
  }, [cacheKey, enabled]);

  return { data, error, isLoading, close };
}

export default usePostEventSource;
